// src/components/GreetingCard/PreviewEnvelope.jsx

import React from 'react'
import { CARD_W, CARD_H } from '../../utils/geometry'
import GreetingCardCanvas from './GreetingCardCanvas'
import VideoCanvas from './VideoCanvas'
import Confetti from 'react-confetti'

export default function PreviewEnvelope({
  opened,
  flipped,
  imageUrl,
  videoUrl,        // NEW: Video URL for video greeting cards
  mediaType = "image", // NEW: "image" or "video"
  text,
  setText,
  inside,
  setInside,
  insideFont,
  signatures = [],
  cardWidth = CARD_W,
  cardHeight = CARD_H,
  insideFontSize,
  insideFill,
  insideBorderColor = "#d4af37",
  insideBorderWidth = 9,
  insideBorderStyle = "solid",
  // Cover border/overlay props
  coverBorderColor,
  coverBorderWidth = 0,
  coverBorderStyle = "solid",
  coverOverlay = "none",
  coverOverlayColor = "#1F2937",
  // Inside text layout props for proper positioning
  insideLayout,
  // Multiple text entries for collaboration mode
  insideTextEntries = null,
}) {
  // Determine if this is a video card
  const isVideo = mediaType === "video" || (videoUrl && !imageUrl);
  // Use the actual card dimensions
  const W = cardWidth
  const H = cardHeight
  // Flap is 25% of card height
  const flapH = Math.round(H * 0.25)
  const pocketH = H - flapH

  // Scale the card slightly to fit within envelope
  const CARD_SCALE = 0.9

  // Inside text style values with sensible fallbacks
  const INSIDE_FONT_SIZE = insideFontSize || 20;
  const INSIDE_FILL = insideFill || '#1f2937';

  // Ensure we never propagate `draggable: false` back to the main editor state
  const handleTextChange = (props) => {
    if (typeof props !== 'object' || props === null) {
      setText(props)
      return
    }
    const { draggable: _omitDraggable, ...cleanProps } = props
    setText(cleanProps)
  }

  const getScaledTextProps = (textProps) => {
    if (!textProps) return textProps
    return {
      ...textProps,
      text: textProps.text || "",
      draggable: false,
    }
  }

  return (
    <div
      className="relative"
      style={{ width: W, height: H, perspective: 1500 }}
    >
      {/* Confetti */}
      {opened && (
        <Confetti
          width={W}
          height={H}
          numberOfPieces={100}
          recycle={false}
          className="absolute top-0 left-0 z-30"
        />
      )}

      {/* Envelope shadow */}
      <div
        className="absolute bg-black/10 rounded-full z-0"
        style={{
          width: W * 0.9,
          height: 20,
          bottom: -15,
          left: W * 0.05,
          filter: 'blur(10px)',
        }}
      />

      {/* Envelope Flap */}
      <div
        className="absolute bg-indigo-200 border-2 border-indigo-300 z-10"
        style={{
          width: W,
          height: flapH,
          top: 0,
          left: 0,
          transform: opened ? 'rotateX(-180deg)' : 'rotateX(0)',
          transformOrigin: 'bottom center',
          opacity: opened ? 0 : 1,
          transition: 'transform 0.8s cubic-bezier(0.34,1.56,0.64,1), opacity 0.6s',
          borderRadius: '12px 12px 0 0',
        }}
      />

      {/* Card */}
      <div
        className="absolute"
        style={{
          top: opened ? 0 : flapH,
          left: 0,
          width: W,
          height: H,
          opacity: opened ? 1 : 0,
          visibility: opened ? 'visible' : 'hidden',
          transform: opened ? 'none' : `translateY(${pocketH}px)`,
          transition: 'transform 1.1s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease, visibility 0.3s ease',
        }}
      >
        <div
          className="relative w-full h-full flex items-center justify-center"
          style={{
            transformStyle: 'preserve-3d',
            transform: `${flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'} scale(${CARD_SCALE})`,
            transformOrigin: 'center center',
            transition: 'transform 1.1s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          {/* Front Face */}
          <div
            className="absolute inset-0 shadow-lg bg-white rounded-md border border-gray-300 overflow-hidden"
            style={{ backfaceVisibility: 'hidden' }}
          >
            {isVideo ? (
              /* Video greeting card front with text and frame overlay */
              <div className="relative w-full h-full overflow-hidden">
                <VideoCanvas
                  videoSrc={videoUrl}
                  cardWidth={cardWidth}
                  cardHeight={cardHeight}
                  autoPlay={opened}
                  loop={true}
                  muted={true}
                  showControls={false}
                  isPreview={true}
                  className="absolute inset-0 w-full h-full"
                />
                
                {/* Frame/Border overlay for video - positioned to wrap exactly */}
                {coverBorderWidth > 0 && coverBorderColor && (
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      border: `${coverBorderWidth}px ${coverBorderStyle} ${coverBorderColor}`,
                      boxSizing: 'border-box',
                      zIndex: 10,
                    }}
                  />
                )}
                
                {/* Text overlay for video */}
                {text?.text && (
                  <div
                    className={`absolute pointer-events-none ${
                      text.animStyle === 'bounce' ? 'animate-bounce-subtle' :
                      text.animStyle === 'wiggle' ? 'animate-wiggle' : ''
                    }`}
                    style={{
                      left: text.x || cardWidth * 0.1,
                      top: text.y || cardHeight * 0.1,
                      width: text.width || cardWidth * 0.8,
                      fontFamily: text.fontFamily || 'Lilita One',
                      fontSize: text.fontSize || 48,
                      fontWeight: text.fontWeight || 700,
                      color: text.fill || '#000000',
                      textAlign: text.align || 'center',
                      lineHeight: text.lineHeight || 1.2,
                      letterSpacing: text.letterSpacing || 1,
                      WebkitTextStroke: text.stroke && text.strokeWidth 
                        ? `${text.strokeWidth}px ${text.stroke}` 
                        : 'none',
                      transform: text.rotation ? `rotate(${text.rotation}deg)` : 'none',
                      whiteSpace: 'pre-wrap',
                      wordWrap: 'break-word',
                      // CSS animation fallback for video cards
                      animation: text.animStyle === 'bounce' 
                        ? 'bounceSubtle 1s ease-in-out infinite' 
                        : text.animStyle === 'wiggle'
                        ? 'wiggle 0.5s ease-in-out infinite'
                        : 'none',
                    }}
                  >
                    {text.text}
                  </div>
                )}
                
                {/* CSS animations for video card text */}
                <style>{`
                  @keyframes bounceSubtle {
                    0%, 100% { transform: scale(1) ${text?.rotation ? `rotate(${text.rotation}deg)` : ''}; }
                    50% { transform: scale(1.03) ${text?.rotation ? `rotate(${text.rotation}deg)` : ''}; }
                  }
                  @keyframes wiggle {
                    0%, 100% { transform: rotate(${(text?.rotation || 0) - 2}deg); }
                    50% { transform: rotate(${(text?.rotation || 0) + 2}deg); }
                  }
                `}</style>
                
                {/* Decorative overlay for video */}
                {coverOverlay && coverOverlay !== 'none' && (
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: coverOverlay === 'snow-splatter' 
                        ? 'radial-gradient(circle, white 2px, transparent 2px)'
                        : coverOverlay === 'stars'
                        ? 'radial-gradient(circle, gold 1px, transparent 1px)'
                        : 'none',
                      backgroundSize: coverOverlay === 'snow-splatter' ? '30px 30px' : '40px 40px',
                      opacity: 0.6,
                    }}
                  />
                )}
              </div>
            ) : (
              /* Image greeting card front */
              <GreetingCardCanvas
                side="front"
                bgSrc={imageUrl}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                textProps={getScaledTextProps(text)}
                setText={handleTextChange}
                isPreview={true}
                coverBorderColor={coverBorderColor}
                coverBorderWidth={coverBorderWidth}
                coverBorderStyle={coverBorderStyle}
                coverOverlay={coverOverlay}
                coverOverlayColor={coverOverlayColor}
              />
            )}
          </div>

          {/* Back Face - uses GreetingCardCanvas for proper text positioning */}
          <div
            className="absolute inset-0 shadow-lg rounded-md overflow-hidden"
            style={{
              transform: 'rotateY(180deg)',
              backfaceVisibility: 'hidden',
            }}
          >
            <GreetingCardCanvas
              side="inside"
              cardWidth={cardWidth}
              cardHeight={cardHeight}
              textProps={{
                text: inside || "",
                x: insideLayout?.x,
                y: insideLayout?.y,
                width: insideLayout?.width,
                fontSize: INSIDE_FONT_SIZE,
                fontFamily: insideFont || "'Inter', sans-serif",
                fill: INSIDE_FILL,
                align: insideLayout?.align || "left",
                draggable: false,
              }}
              setText={() => {}} // No editing in preview
              isPreview={true}
              insideBorderColor={insideBorderColor}
              insideBorderWidth={insideBorderWidth}
              insideBorderStyle={insideBorderStyle}
              // Multiple text entries for collaboration preview (all non-draggable)
              insideTextEntries={insideTextEntries?.map(entry => ({
                ...entry,
                draggable: false, // No dragging in preview
              }))}
              onTextEntryChange={() => {}} // No editing in preview
            />
            {/* Signatures - only render if not already included in inside text */}
            {/* Skip rendering separate signatures since they're typically part of the inside text */}
          </div>
        </div>
      </div>

      {/* Envelope Pocket */}
      <div
        className="absolute bg-indigo-100 border-2 border-indigo-300 rounded-b-xl"
        style={{
          width: W,
          height: pocketH,
          bottom: 0,
          left: 0,
          opacity: opened ? 0 : 1,
          transform: opened ? 'translateY(10px)' : 'translateY(0)',
          transition: 'opacity 0.6s, transform 0.5s',
          pointerEvents: 'none',
        }}
      />
    </div>
  )
}
